<template>
  <div>
    <!--
      AV 23 a AV 28 — canal WhatsApp.

      Esta tela existe para que a pessoa avaliadora consiga ver, sem Postman e sem SQL:
        · se o canal está credenciado (AV 23) — e o aviso quando não está;
        · a identidade institucional do número (AV 26);
        · que o número é ÚNICO e a segregação é por sessão (AV 27);
        · o código de incorporação da página institucional (AV 28);
        · o histórico auditável dos relatórios entregues (AV 24).

      Nenhuma regra de negócio aqui. Nenhuma credencial é exibida.
    -->

    <el-alert v-if="erro" type="error" :closable="false" show-icon data-test="erro"
              :title="erro" class="u-mb-10"></el-alert>

    <!-- ============ AV 23: o aviso que impede leitura errada da POC ============ -->
    <el-alert v-if="canal.simulado" type="warning" :closable="false" show-icon
              data-test="aviso-simulado" class="u-mb-10"
              title="Número do WhatsApp não credenciado">
      <div class="obs">
        Pendente de credenciamento na Meta.
      </div>
    </el-alert>

    <el-row :gutter="10" class="u-mb-10">
      <el-col :xs="12" :sm="6"><div class="cartao" :class="{destaque: !canal.simulado}">
        <div class="rot">canal WhatsApp</div>
        <div class="val" data-test="situacao-canal">
          {{ canal.simulado ? 'simulado' : 'credenciado' }}
        </div>
      </div></el-col>
      <el-col :xs="12" :sm="6"><div class="cartao">
        <div class="rot">interpretação (LLM)</div>
        <div class="val" data-test="situacao-llm">
          {{ (canal.llm && canal.llm.configurado) ? 'ativo' : 'inativo' }}
        </div>
        <div class="obs">AV 25</div>
      </div></el-col>
      <el-col :xs="12" :sm="6"><div class="cartao">
        <div class="rot">transcrição (voz)</div>
        <div class="val" data-test="situacao-stt">
          {{ (canal.stt && canal.stt.configurado) ? 'ativo' : 'inativo' }}
        </div>
        <div class="obs">AV 25</div>
      </div></el-col>
      <el-col :xs="12" :sm="6"><div class="cartao">
        <div class="rot">número único</div>
        <div class="val" data-test="numero-exibicao">
          {{ (canal.numero && canal.numero.exibicao) || '—' }}
        </div>
        <div class="obs">AV 27</div>
      </div></el-col>
    </el-row>

    <el-row :gutter="10">
      <!-- ============ AV 26 ============ -->
      <el-col :xs="24" :sm="12">
        <c-panel title="Identidade institucional (AV 26)" titleIcon="fas fa-id-badge">
          <!--
            O canal precisa ser ESCOLHÍVEL.
            O site é servido por rota nossa e funciona hoje; o WhatsApp depende do
            credenciamento na Meta. Com o canal fixo em WhatsApp, a customização do avatar
            ficava impossível de demonstrar sem credencial.
          -->
          <div class="u-mb-10">
            <el-radio-group v-model="canalIdentidade" size="mini" @change="carregar"
                            data-test="canal-identidade">
              <el-radio-button label="site">Site (widget)</el-radio-button>
              <el-radio-button label="whatsapp">WhatsApp</el-radio-button>
            </el-radio-group>
            <span class="obs u-ml-10" v-if="canalIdentidade === 'site'">
              aplicado imediatamente — não depende da Meta
            </span>
            <span class="obs u-ml-10" v-else>
              a publicação no número exige credenciamento na Meta
            </span>
          </div>

          <table class="table table-striped" data-test="identidade">
            <tr><td style="width:120px">Nome</td><td>{{ id.nome || '—' }}</td></tr>
            <tr><td>Sobre</td><td>{{ id.sobre || '—' }}</td></tr>
            <tr><td>Descrição</td><td>{{ id.descricao || '—' }}</td></tr>
            <tr><td>E-mail</td><td>{{ id.email || '—' }}</td></tr>
            <tr><td>Endereço</td><td>{{ id.endereco || '—' }}</td></tr>
            <tr><td>Site</td><td>{{ id.site || '—' }}</td></tr>
          </table>

          <!--
            AV 26 — CUSTOMIZAÇÃO DO AVATAR.

            O ciclo inteiro pela interface: selecionar → validar → armazenar → visualizar →
            substituir/remover → publicar. A validação real é do servidor, que lê o CONTEÚDO
            do arquivo; aqui só se restringe o seletor por conveniência.
          -->
          <div class="avatar-box">
            <div class="avatar-previa">
              <img v-if="avatarSrc" :src="avatarSrc" alt="Avatar do atendimento"
                   data-test="avatar-previa" />
              <div v-else class="avatar-vazio" data-test="avatar-vazio">
                sem avatar configurado
              </div>
            </div>

            <div class="avatar-acoes">
              <input type="file" ref="arquivoAvatar" accept="image/jpeg,image/png"
                     data-test="avatar-input" @change="enviarAvatar" style="display:none" />

              <el-button size="mini" type="primary" icon="fas fa-image" data-test="avatar-escolher"
                         :loading="enviandoAvatar" @click="escolherAvatar">
                {{ avatar.configurado ? 'Substituir avatar' : 'Escolher avatar' }}
              </el-button>

              <el-button class="u-ml-10" v-if="avatar.configurado" size="mini" type="danger"
                         data-test="avatar-remover" :loading="enviandoAvatar"
                         @click="removerAvatar">Remover</el-button>

              <div class="obs" data-test="avatar-info">
                <template v-if="avatar.configurado">
                  {{ avatar.largura }}×{{ avatar.altura }} px ·
                  {{ Math.round((avatar.bytes || 0) / 1024) }} KB ·
                  atualizado em {{ avatar.atualizado_em }}
                </template>
                <template v-else>
                  JPEG ou PNG, quadrada, mínimo 192×192 px, até 5 MB.
                </template>
              </div>
            </div>
          </div>

          <el-button type="primary" size="small" icon="fas fa-upload" data-test="publicar-perfil"
                     :loading="publicando" @click="publicarPerfil">
            Publicar perfil no número
          </el-button>

          <div v-if="perfil" class="u-mt-10">
            <el-alert :type="perfil.publicado ? 'success' : 'info'" :closable="false" show-icon
                      data-test="resultado-perfil"
                      :title="perfil.publicado ? 'Perfil publicado no número.'
                                               : 'Perfil montado, não publicado.'">
              <div class="obs" v-if="perfil.detalhe">{{ perfil.detalhe }}</div>
            </el-alert>

            <!--
              A FOTO é uma fronteira SEPARADA dos textos, e a tela precisa dizer isso.
              "Perfil publicado" sem essa distinção faria supor que o cidadão já vê a imagem.
            -->
            <div class="obs u-mt-10" data-test="fronteira-avatar">
              Avatar guardado no sistema:
              <strong>{{ perfil.avatar_local ? 'sim' : 'não' }}</strong> ·
              publicado no número:
              <strong>{{ perfil.avatar_publicado ? 'sim' : 'não' }}</strong>
            </div>

            <pre class="payload" data-test="payload-perfil">{{ perfil.payload }}</pre>
          </div>
        </c-panel>
      </el-col>

      <!-- ============ AV 27 e AV 28 ============ -->
      <el-col :xs="24" :sm="12">
        <c-panel title="Número único (AV 27)" titleIcon="fas fa-phone">
          <table class="table table-striped" data-test="numero">
            <tr>
              <td style="width:150px">Configurado</td>
              <td>{{ (canal.numero && canal.numero.configurado) ? 'sim' : 'não' }}</td>
            </tr>
            <tr>
              <td>Identificador</td>
              <td>{{ (canal.numero && canal.numero.phone_number_id) || '—' }}</td>
            </tr>
          </table>
          <div class="obs" data-test="obs-numero">
            {{ (canal.numero && canal.numero.observacao) || '' }}
          </div>
        </c-panel>

        <c-panel title="Incorporar na página institucional (AV 28)" titleIcon="fas fa-code"
                 class="u-mt-10">
          <el-form :inline="true" size="mini" class="u-mb-10">
            <el-form-item label="Escola">
              <el-select v-model="embedInstituicao" clearable placeholder="rede toda"
                         style="width:200px" @change="gerarEmbed">
                <el-option v-for="i in instituicoes" :key="i.id" :label="i.nome" :value="i.id">
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="Origem">
              <el-input v-model="embedOrigem" style="width:150px" @change="gerarEmbed"
                        data-test="embed-origem"></el-input>
            </el-form-item>
          </el-form>

          <div v-if="embed">
            <div class="obs u-mb-10">
              O contexto viaja no texto de primeiro contato — é o único transporte que o
              WhatsApp oferece. Ele é uma <strong>dica de origem</strong>, não prova: o cidadão
              pode editá-lo, e o servidor o trata como palpite.
            </div>
            <pre class="payload" data-test="embed-html">{{ embed.html || embed.aviso }}</pre>
          </div>
        </c-panel>
      </el-col>
    </el-row>

    <!-- ============ AV 24 ============ -->
    <c-panel title="Relatórios entregues pelo WhatsApp (AV 24)" titleIcon="fas fa-file-pdf"
             class="u-mt-10">
      <table class="table table-striped" data-test="relatorios">
        <thead>
          <tr>
            <th>Protocolo</th><th>Relatório</th><th>Solicitante</th>
            <th>Escola</th><th>Situação</th><th>Gerado</th><th>Enviado</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in relatorios" :key="r.id">
            <td>{{ r.protocolo }}</td>
            <td>{{ r.relatorio }}</td>
            <td>{{ r.usuario || '—' }}</td>
            <td>{{ r.instituicao || 'rede' }}</td>
            <td>
              <span :class="'sit sit-' + r.situacao">{{ r.situacao }}</span>
              <div class="obs" v-if="r.detalhe">{{ r.detalhe }}</div>
            </td>
            <td>{{ r.gerado_em || '—' }}</td>
            <td>{{ r.enviado_em || '—' }}</td>
          </tr>
          <tr v-if="!relatorios.length">
            <td colspan="7" class="obs" data-test="sem-relatorios">
              Nenhum relatório solicitado pelo WhatsApp até agora.
            </td>
          </tr>
        </tbody>
      </table>
    </c-panel>
  </div>
</template>

<script>
import assistenteApi from '../assistenteApi'

export default {
  name: 'AssistenteCanalWhatsapp',

  data () {
    return {
      api: null,
      canal: {},
      relatorios: [],
      instituicoes: [],
      perfil: null,
      /*
       * QUAL CANAL a identidade está editando.
       *
       * A tela usava 'whatsapp' fixo nas cinco chamadas. O AV 26 (customização do avatar)
       * ficava, por isso, inteiramente refém do credenciamento na Meta — mesmo o canal SITE,
       * que é servido por rota nossa e não depende de ninguém.
       */
      canalIdentidade: 'site',
      identidade: {},
      avatarSrc: null,
      enviandoAvatar: false,
      embed: null,
      embedInstituicao: null,
      embedOrigem: 'site',
      publicando: false,
      erro: null
    }
  },

  computed: {
    /*
     * A identidade vem da TABELA (fonte de verdade), com o bloco do canal como reserva.
     * O `.env` deixou de responder "existe avatar?" — ele só semeia valores iniciais.
     */
    id () { return this.identidade.canal ? this.identidade : (this.canal.identidade || {}) },
    avatar () { return this.identidade.avatar || { configurado: false } }
  },

  mounted () {
    this.api = assistenteApi.criar({ axios: this.$axios, base: this.base(), auth: this.auth() })
    this.carregar()
  },

  methods: {
    base () { return this.$store.state.config.baseUrlApi },
    auth () { return this.$store.getters.getHeaderAuth },

    /* O backend nega permissão com HTTP 200 + {erro:{cod:800}}, não com 403. */
    textoErro (e) {
      const r = e && e.response ? e.response : null
      if (!r) return 'Falha de comunicação com o servidor.'
      if (r.data && typeof r.data.erro === 'string') return r.data.erro
      if (r.data && r.data.erro && r.data.erro.text) return r.data.erro.text
      return 'Erro ' + r.status + ' ao consultar o servidor.'
    },

    carregar () {
      const self = this
      this.erro = null

      this.api.whatsappCanal().then(function (r) {
        self.canal = r.data || {}
        self.gerarEmbed()
      }).catch(function (e) { self.erro = self.textoErro(e) })

      this.api.identidadeCanal(this.canalIdentidade).then(function (r) {
        self.identidade = r.data || {}
        self.carregarAvatar()
      }).catch(function (e) { self.erro = self.textoErro(e) })

      this.api.whatsappRelatorios().then(function (r) {
        self.relatorios = (r.data && r.data.itens) || []
      }).catch(function (e) { self.erro = self.textoErro(e) })

      this.$axios.get(this.base() + '/api/instituicao/getAll', this.auth())
        .then(function (r) {
          self.instituicoes = (r.data && (r.data.instituicoes || r.data)) || []
        }).catch(function () { self.instituicoes = [] })
    },

    escolherAvatar () {
      this.$refs.arquivoAvatar.click()
    },

    /**
     * Busca a imagem com o cabeçalho de autenticação e a exibe como blob.
     *
     * `<img src>` direto não funcionaria: o arquivo vive em disco privado, atrás de rota
     * autenticada, e a tag não carrega o token.
     */
    carregarAvatar () {
      const self = this
      if (!this.avatar.configurado) { this.avatarSrc = null; return }

      const cfg = Object.assign({ responseType: 'blob' }, this.auth())

      this.$axios.get(this.api.identidadeAvatarUrl(this.canalIdentidade), cfg)
        .then(function (r) {
          if (self.avatarSrc) URL.revokeObjectURL(self.avatarSrc)
          self.avatarSrc = URL.createObjectURL(r.data)
        })
        .catch(function () { self.avatarSrc = null })
    },

    enviarAvatar (ev) {
      const arquivo = ev.target.files && ev.target.files[0]
      if (!arquivo || this.enviandoAvatar) return

      const self = this
      this.enviandoAvatar = true
      this.erro = null

      const fd = new FormData()
      fd.append('avatar', arquivo)

      const headers = Object.assign({}, this.auth())
      headers.headers = Object.assign({}, headers.headers,
        { 'Content-Type': 'multipart/form-data' })

      this.api.identidadeAvatar(this.canalIdentidade, fd, headers).then(function (r) {
        self.identidade = r.data.identidade || {}
        self.enviandoAvatar = false
        self.$refs.arquivoAvatar.value = ''
        self.carregarAvatar()
        window.eventBus.$emit('alert', { msg: 'Avatar atualizado.', type: 'success' })
      }).catch(function (e) {
        self.erro = self.textoErro(e)
        self.enviandoAvatar = false
        self.$refs.arquivoAvatar.value = ''
      })
    },

    removerAvatar () {
      if (this.enviandoAvatar) return
      const self = this
      this.enviandoAvatar = true

      this.api.identidadeAvatarRemover(this.canalIdentidade).then(function (r) {
        self.identidade = r.data.identidade || {}
        self.avatarSrc = null
        self.enviandoAvatar = false
        window.eventBus.$emit('alert', { msg: 'Avatar removido.', type: 'success' })
      }).catch(function (e) {
        self.erro = self.textoErro(e)
        self.enviandoAvatar = false
      })
    },

    publicarPerfil () {
      if (this.publicando) return
      const self = this
      this.publicando = true

      this.api.identidadePublicar(this.canalIdentidade).then(function (r) {
        self.perfil = r.data
        self.publicando = false
        self.carregar()
        window.eventBus.$emit('alert', {
          msg: r.data.publicado ? 'Textos do perfil publicados.'
                                : 'Perfil montado — publicação exige o número credenciado.',
          type: r.data.publicado ? 'success' : 'warning'
        })
      }).catch(function (e) {
        self.erro = self.textoErro(e)
        self.publicando = false
      })
    },

    gerarEmbed () {
      const self = this
      this.api.whatsappEmbed({
        instituicao_id: this.embedInstituicao,
        origem: this.embedOrigem
      }).then(function (r) {
        self.embed = r.data
      }).catch(function (e) { self.erro = self.textoErro(e) })
    }
  }
}
</script>

<style scoped>
.cartao { border: 1px solid #ebeef5; border-radius: 4px; padding: 10px; }
.cartao.destaque { border-color: #67c23a; }
.rot { font-size: 11px; color: #909399; text-transform: uppercase; }
.val { font-size: 20px; font-weight: 600; }
.obs { font-size: 11px; color: #909399; }
.avatar-box { display: flex; gap: 12px; align-items: flex-start; margin: 10px 0; }
.avatar-previa img { width: 96px; height: 96px; object-fit: cover; border-radius: 6px;
                     border: 1px solid #ebeef5; }
.avatar-vazio { width: 96px; height: 96px; border: 1px dashed #dcdfe6; border-radius: 6px;
                display: flex; align-items: center; justify-content: center; text-align: center;
                font-size: 10px; color: #909399; padding: 4px; }
.avatar-acoes { flex: 1; }
.payload { background: #f5f7fa; padding: 8px; font-size: 11px; white-space: pre-wrap;
           word-break: break-all; max-height: 220px; overflow: auto; }
.sit { padding: 1px 6px; border-radius: 3px; font-size: 11px; }
.sit-enviado { background: #f0f9eb; color: #67c23a; }
.sit-gerado { background: #fdf6ec; color: #e6a23c; }
.sit-falha_geracao, .sit-falha_envio { background: #fef0f0; color: #f56c6c; }
.sit-expirado, .sit-pendente { background: #f4f4f5; color: #909399; }
.u-mb-10 { margin-bottom: 10px; }
.u-mt-10 { margin-top: 10px; }
</style>
